import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input, Button, Alert, AlertDescription, Spinner, ProgressBar } from '@/components/ui';
import { Mail, Lock, User, UserPlus } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { cn } from '@/lib/utils';
import { CURRENT_LEGAL_VERSION } from '@/legal/legalVersions';

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup, loginWithGoogle, sendMagicLink, currentUser, loading: authLoading } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingInvitation, setCheckingInvitation] = useState(true);
  const [hasInvitation, setHasInvitation] = useState(false);

  // "You already have an account" recovery — shown when signup hits
  // auth/email-already-in-use (e.g. a pre-provisioned or previously-registered
  // invitee clicking the invite link). Instead of a dead-end error we offer a
  // one-click passwordless sign-in link into the existing account.
  const [existingAccount, setExistingAccount] = useState(false);
  const [magicSentTo, setMagicSentTo] = useState<string | null>(null);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicError, setMagicError] = useState('');

  // Password strength
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState('');

  // Check for invitation token in URL or email
  useEffect(() => {
    const checkInvitation = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      const emailParam = params.get('email');
      
      if (emailParam) {
        setEmail(emailParam);
      }

      // If there's a token in URL, user has an invitation
      if (token) {
        setHasInvitation(true);
        setCheckingInvitation(false);
        return;
      }

      // If email is provided, check for pending invitation
      if (emailParam) {
        try {
          const inviteQuery = query(
            collection(db, 'invitations'),
            where('email', '==', emailParam.toLowerCase()),
            where('status', '==', 'pending')
          );
          const inviteSnapshot = await getDocs(inviteQuery);
          
          if (!inviteSnapshot.empty) {
            setHasInvitation(true);
            setCheckingInvitation(false);
            return;
          }
        } catch (err) {
          console.error('Error checking invitation:', err);
        }
      }

      // Otherwise, signup is invite-only - show message
      setCheckingInvitation(false);
    };

    checkInvitation();
  }, [location]);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && currentUser) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [currentUser, authLoading, navigate, location]);

  // Calculate password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      setPasswordFeedback('');
      return;
    }

    let strength = 0;
    let feedback = '';

    // Length check
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;

    // Character variety checks
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^A-Za-z0-9]/.test(password)) strength += 15;

    // Determine feedback
    if (strength < 30) feedback = 'Weak';
    else if (strength < 60) feedback = 'Fair';
    else if (strength < 80) feedback = 'Good';
    else feedback = 'Strong';

    setPasswordStrength(strength);
    setPasswordFeedback(feedback);
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // CRITICAL: Check if user has invitation
    if (!hasInvitation) {
      setError('Signup is invite-only. Please use the invitation link sent to your email.');
      return;
    }

    // Validation
    if (!displayName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!acceptTerms) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, displayName);
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Signup error:', err);
      // Already-registered invitee (or a pre-provisioned account): don't
      // dead-end. Offer a passwordless sign-in link into the existing account.
      if (err?.code === 'auth/email-already-in-use') {
        setExistingAccount(true);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMagicLink = async () => {
    setMagicError('');
    setMagicLoading(true);
    try {
      await sendMagicLink(email);
      setMagicSentTo(email);
    } catch (err: any) {
      console.error('Magic link error:', err);
      setMagicError('Could not send the sign-in link. Please try again, or sign in from the login page.');
    } finally {
      setMagicLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    // CRITICAL: Check if user has invitation
    if (!hasInvitation) {
      setError('Signup is invite-only. Please use the invitation link sent to your email.');
      return;
    }

    if (!acceptTerms) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Pass the version the user just consented to on this form so the
      // server-side invite-accept records legal acceptance — no second gate.
      await loginWithGoogle(CURRENT_LEGAL_VERSION);
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Google signup error:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error: any): string => {
    const code = error.code || '';
    if (code === 'auth/email-already-in-use') {
      return 'An account with this email already exists';
    }
    if (code === 'auth/invalid-email') {
      return 'Invalid email address';
    }
    if (code === 'auth/weak-password') {
      return 'Password is too weak. Please use a stronger password';
    }
    if (code === 'auth/operation-not-allowed') {
      return 'Email/password accounts are not enabled';
    }
    if (code === 'auth/popup-closed-by-user') {
      return 'Sign-up popup was closed';
    }
    if (code === 'auth/cancelled-popup-request') {
      return 'Only one popup can be opened at a time';
    }
    return error.message || 'An error occurred during signup';
  };

  // Show spinner while checking auth state or invitation
  if (authLoading || checkingInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Spinner size="lg" className="text-gray-400" />
      </div>
    );
  }

  // Already-registered email: offer a passwordless sign-in link instead of a
  // dead-end "email already exists" error. Handles pre-provisioned owners and
  // returning invitees who click the invite link but already have an account.
  if (existingAccount) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <img src="/logo.png" alt="simcheck.ai" className="w-14 h-14 object-contain mb-6 mx-auto" />
            {magicSentTo ? (
              <>
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Check your email</h1>
                <p className="text-gray-500 mb-6">
                  We sent a secure sign-in link to <strong className="text-gray-900">{magicSentTo}</strong>.
                  Open it on this device to sign in — no password needed.
                </p>
                <p className="text-sm text-gray-400 mb-6">
                  Didn't get it? Check spam, or{' '}
                  <button
                    type="button"
                    onClick={handleSendMagicLink}
                    disabled={magicLoading}
                    className="font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50"
                  >
                    send it again
                  </button>.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-gray-900 mb-4">You already have an account</h1>
                <p className="text-gray-500 mb-6">
                  An account for <strong className="text-gray-900">{email}</strong> already exists.
                  We can email you a secure sign-in link so you can get in without a password.
                </p>
                {magicError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertDescription>{magicError}</AlertDescription>
                    </Alert>
                  </div>
                )}
                <Button
                  onClick={handleSendMagicLink}
                  disabled={magicLoading}
                  variant="default"
                  className="w-full mb-3"
                >
                  {magicLoading ? <Spinner size="sm" /> : (<><Mail className="w-4 h-4 mr-2" />Email me a sign-in link</>)}
                </Button>
              </>
            )}
            <p className="text-sm text-gray-400">
              Prefer a password?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show invite-only message if no invitation
  if (!hasInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <img src="/logo.png" alt="simcheck.ai" className="w-14 h-14 object-contain mb-6 mx-auto" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Signup is Invite-Only
            </h1>
            <p className="text-gray-500 mb-6">
              To create an account, you need an invitation link sent to your email address.
              Please contact your organization administrator to receive an invitation.
            </p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full btn btn-primary"
              >
                Go to Sign In
              </Link>
              <p className="text-sm text-gray-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white light" style={{
      // Force light-mode CSS variables so components render correctly
      // on the white background regardless of user's theme preference
      '--foreground': '0 0% 3.9%',
      '--background': '0 0% 100%',
      '--primary': '0 0% 9%',
      '--primary-foreground': '0 0% 98%',
      '--secondary': '0 0% 96.1%',
      '--secondary-foreground': '0 0% 9%',
      '--muted': '0 0% 96.1%',
      '--muted-foreground': '0 0% 45.1%',
      '--accent': '0 0% 96.1%',
      '--accent-foreground': '0 0% 9%',
      '--border': '0 0% 89.8%',
      '--input': '0 0% 89.8%',
      '--ring': '0 0% 3.9%',
      '--destructive': '0 84.2% 60.2%',
      '--destructive-foreground': '0 0% 98%',
      color: 'hsl(0 0% 3.9%)',
    } as React.CSSProperties}>
      {/* Left side - Signup form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Logo and Title */}
          <div>
            <img src="/logo.png" alt="simcheck.ai" className="w-14 h-14 object-contain mb-6" />
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Create account
            </h1>
            <p className="text-sm text-gray-500">
              Start testing your telecom infrastructure today
            </p>
          </div>

          <div className="mt-8">
            {/* Error Alert */}
            {error && (
              <div className="mb-6">
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              </div>
            )}

            {/* Terms checkbox - applies to both signup methods */}
            <div className="flex items-start mb-6">
              <input
                id="accept-terms"
                name="accept-terms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500 focus:ring-offset-0 mt-0.5"
              />
              <label
                htmlFor="accept-terms"
                className="ml-2 block text-sm text-gray-600"
              >
                I accept the{' '}
                <a
                  href="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            {/* Google Sign-Up Button */}
            <div>
              <Button
                onClick={handleGoogleSignup}
                disabled={loading}
                variant="secondary"
                className="w-full"
              >
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </>
                )}
              </Button>
            </div>

            {/* Divider */}
            <div className="mt-6 relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-2 bg-white text-gray-400">
                  Or sign up with email
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Input
                label="Full name"
                type="text"
                name="displayName"
                autoComplete="name"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your full name"
                disabled={loading}
              />

              <Input
                label="Email address"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={loading}
              />

              <div>
                <Input
                  label="Password"
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  disabled={loading}
                  helperText="Minimum 8 characters"
                />
                {password && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-500">
                        Password strength:
                      </span>
                      <span
                        className={`font-medium ${
                          passwordStrength < 30
                            ? 'text-accent-red'
                            : passwordStrength < 60
                            ? 'text-accent-orange'
                            : passwordStrength < 80
                            ? 'text-accent-yellow'
                            : 'text-accent-green'
                        }`}
                      >
                        {passwordFeedback}
                      </span>
                    </div>
                    <ProgressBar
                      value={passwordStrength}
                      className={cn(
                        "h-1",
                        passwordStrength < 30
                          ? '[&>div]:bg-status-error'
                          : passwordStrength < 60
                          ? '[&>div]:bg-status-warning'
                          : passwordStrength < 80
                          ? '[&>div]:bg-primary'
                          : '[&>div]:bg-status-success'
                      )}
                    />
                  </div>
                )}
              </div>

              <Input
                label="Confirm password"
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                disabled={loading}
                helperText={
                  confirmPassword && password !== confirmPassword
                    ? 'Passwords do not match'
                    : undefined
                }
                className={confirmPassword && password !== confirmPassword ? 'border-status-error' : ''}
              />

              <div>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="default"
                  className="w-full"
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create account
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Sign in link */}
            <p className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Brand/Hero Section */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-primary to-accent-blue flex flex-col items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="text-5xl font-bold mb-6">
              Join simcheck.ai
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-md">
              The AI-powered eSIM assurance platform
            </p>
            <div className="grid grid-cols-3 gap-8 max-w-2xl mb-12">
              <div>
                <div className="text-4xl font-bold mb-2">SGP.32</div>
                <div className="text-sm opacity-80">Provisioning Ready</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">99.9%</div>
                <div className="text-sm opacity-80">Platform Uptime</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">24/7</div>
                <div className="text-sm opacity-80">Automated Testing</div>
              </div>
            </div>
            <div className="flex flex-col gap-4 text-left max-w-md">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-white mt-2" />
                <div>
                  <h3 className="font-semibold mb-1">Chat to Create Tests</h3>
                  <p className="text-sm opacity-80">
                    Describe a test in plain language — the AI builds and runs it on real devices
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-white mt-2" />
                <div>
                  <h3 className="font-semibold mb-1">eSIM &amp; SGP.32 Provisioning</h3>
                  <p className="text-sm opacity-80">
                    Validate eSIM download, install, and remote SGP.32 provisioning end to end
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-white mt-2" />
                <div>
                  <h3 className="font-semibold mb-1">Global Roaming &amp; App Testing</h3>
                  <p className="text-sm opacity-80">
                    Test 5G, 4G, voice, SMS, data, and eSIM apps across every roaming partner
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

